import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import SmoothScroll from "@/components/SmoothScroll";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import Login from "@/pages/Login";

function ProtectedRoute({ children }) {
    const { user } = useAuth();
    if (user === null) return <div className="min-h-screen grid place-items-center bg-obsidian text-cyan eyebrow">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function AuthCallback() {
    const { processGoogleSession, setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const done = useRef(false);

    useEffect(() => {
        if (done.current) return;
        done.current = true;
        const params = new URLSearchParams(location.hash.replace(/^#/, ""));
        const sid = params.get("session_id");
        if (!sid) { navigate("/"); return; }
        processGoogleSession(sid)
            .then((data) => {
                window.history.replaceState(null, "", window.location.pathname);
                navigate(data.role === "admin" ? "/admin" : "/");
            })
            .catch(() => { setUser(false); navigate("/login"); });
    }, []);

    return <div className="min-h-screen grid place-items-center bg-obsidian text-cyan eyebrow">Signing you in…</div>;
}

function AppRoutes() {
    const location = useLocation();
    // Detect the OAuth callback synchronously during render (prevents race conditions)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <SmoothScroll>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:slug" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </SmoothScroll>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <LanguageProvider>
                        <Toaster position="top-center" richColors />
                        <AppRoutes />
                    </LanguageProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
